import { Bell, Calendar, ChevronRight, FileText, PenSquare, Settings, Sparkles, Vote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, PageHeader, SectionLabel } from '../components/ui';
import { notificationsAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { timeAgo } from '../lib/time';

interface Notification {
  id: string;
  notification_type?: string;
  title: string;
  message?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  is_read?: boolean;
  read_at?: string | null;
  created_at: string;
}

export default function NotificationsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: notifications, isLoading } = useQuery<Notification[]>(['citizen-notifications'], async () => {
    const { data } = await notificationsAPI.getAll({ limit: 50 });
    return data.data?.notifications || [];
  });

  async function markAllRead() {
    await notificationsAPI.markAllRead();
    queryClient.invalidateQueries({ queryKey: ['citizen-notifications'] });
  }

  const all = notifications || [];
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const today = all.filter((n) => new Date(n.created_at) >= todayStart);
  const recent = all.filter((n) => new Date(n.created_at) < todayStart);

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader
        title="Notifications"
        onBack={() => navigate('/citizen/home')}
        right={
          <Button variant="ghost" size="sm" className="text-xs" onClick={markAllRead}>
            Mark all read
          </Button>
        }
      />

      <div className="px-4 pt-2 pb-24">
        {isLoading && <p className="text-sm text-[color:var(--muted-foreground)] px-1 py-6">Loading…</p>}

        {!isLoading && all.length === 0 && (
          <div className="text-center py-16 space-y-2">
            <Bell className="w-8 h-8 mx-auto text-[color:var(--muted-foreground)]" />
            <p className="text-sm text-[color:var(--muted-foreground)]">
              You're all caught up — nothing new right now.
            </p>
          </div>
        )}

        {today.length > 0 && (
          <>
            <SectionLabel>Today</SectionLabel>
            <Card className="divide-y border-0 overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              {today.map((n) => (
                <NotifRow key={n.id} n={n} />
              ))}
            </Card>
          </>
        )}

        {recent.length > 0 && (
          <div className={today.length > 0 ? 'mt-6' : ''}>
            <SectionLabel>Recent</SectionLabel>
            <Card className="divide-y border-0 overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              {recent.map((n) => (
                <NotifRow key={n.id} n={n} />
              ))}
            </Card>
          </div>
        )}

        <button
          onClick={() => navigate('/citizen/profile')}
          className="w-full mt-6 p-4 rounded-xl flex items-center justify-between hover-elevate border"
          style={{ background: 'transparent', borderStyle: 'dashed', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center gap-3">
            <Settings className="w-4 h-4 text-[color:var(--muted-foreground)]" />
            <span className="text-sm font-semibold">Notification preferences</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)]" />
        </button>
      </div>
    </div>
  );
}

function NotifRow({ n }: { n: Notification }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const type = n.notification_type || '';
  const Icon = type.includes('bill')
    ? FileText
    : type.includes('vote') || type.includes('ballot')
      ? Vote
      : type.includes('calendar') || type.includes('event')
        ? Calendar
        : type.includes('survey') || type.includes('outreach')
          ? PenSquare
          : type.includes('note')
            ? Sparkles
            : Bell;

  const unread = !n.is_read && !n.read_at;

  async function tap() {
    if (unread) {
      notificationsAPI.markRead(n.id).then(() => queryClient.invalidateQueries({ queryKey: ['citizen-notifications'] }));
    }
    if (n.entity_type === 'bill' && n.entity_id) navigate(`/citizen/bill/${n.entity_id}`);
  }

  return (
    <button onClick={tap} className="w-full p-4 flex gap-3 items-start hover-elevate text-left">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{ background: 'var(--accent)', color: 'var(--primary)' }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <p className={cn('text-sm leading-tight', unread ? 'font-bold' : 'font-semibold')}>{n.title}</p>
          <span className="text-[11px] text-[color:var(--muted-foreground)] shrink-0 whitespace-nowrap">
            {timeAgo(n.created_at)}
          </span>
        </div>
        {n.message && <p className="text-xs text-[color:var(--muted-foreground)] mt-1 line-clamp-2">{n.message}</p>}
      </div>
      {unread && <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: 'var(--primary)' }} />}
    </button>
  );
}
